// client/src/pages/CheckinTemplatesPage.jsx
import { useState } from 'react'
import { ChevronDown, ChevronUp, Plus, Trash2, X, Edit2, Copy } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import {
  useCheckinTemplates,
  useCheckinTemplateItems,
  useCreateCheckinTemplate,
  useUpdateCheckinTemplate,
  useDeleteCheckinTemplate,
  useDuplicateCheckinTemplate,
  useCreateCheckinTemplateItem,
  useUpdateCheckinTemplateItem,
  useDeleteCheckinTemplateItem,
} from '../api/hooks'

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

const FUEL_LABELS = { petrol: 'Petrol', diesel: 'Diesel', electric: 'Electric', hybrid: 'Hybrid', any: 'Any' }
const FREQ_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', biannual: 'Biannual', annual: 'Annual' }
const FUEL_STYLES = {
  petrol:   'bg-blue-500/15 text-blue-400',
  diesel:   'bg-slate-500/20 text-slate-400',
  electric: 'bg-green-500/15 text-green-400',
  hybrid:   'bg-teal-500/15 text-teal-400',
  any:      'bg-gray-500/15 text-gray-400',
}
const FREQ_STYLES = {
  monthly:   'bg-indigo-500/15 text-indigo-400',
  quarterly: 'bg-purple-500/15 text-purple-400',
  biannual:  'bg-amber-500/15 text-amber-400',
  annual:    'bg-rose-500/15 text-rose-400',
}
const RESPONSE_TYPES = ['select', 'yesno', 'number', 'text']

// ─── Template modal (create & edit) ──────────────────────────────────────────

function TemplateModal({ existing, onClose }) {
  const create = useCreateCheckinTemplate()
  const update = useUpdateCheckinTemplate()
  const [form, setForm] = useState({
    name:        existing?.name        || '',
    fuelType:    existing?.fuel_type   || '',
    frequency:   existing?.frequency   || 'monthly',
    description: existing?.description || '',
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, ...form })
        toast.success('Template updated')
      } else {
        await create.mutateAsync(form)
        toast.success('Template created')
      }
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save')
    }
  }

  const isPending = create.isPending || update.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">
            {existing ? 'Edit Template' : 'New Check-In Template'}
          </h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Name</label>
            <input type="text" value={form.name} onChange={set('name')} required
              placeholder="e.g. Petrol Monthly" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Fuel type</label>
              <select value={form.fuelType} onChange={set('fuelType')} className={inputCls}>
                <option value="">Any / not set</option>
                {Object.entries(FUEL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Frequency</label>
              <select value={form.frequency} onChange={set('frequency')} className={inputCls} required>
                {Object.entries(FREQ_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Description</label>
            <textarea rows={2} value={form.description} onChange={set('description')}
              className={clsx(inputCls, 'resize-none')} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {isPending ? 'Saving…' : existing ? 'Save Changes' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Add / edit item modal ────────────────────────────────────────────────────

function ItemModal({ templateId, existing, onClose }) {
  const createItem = useCreateCheckinTemplateItem()
  const updateItem = useUpdateCheckinTemplateItem()

  const [form, setForm] = useState({
    category:     existing?.category      || '',
    label:        existing?.label         || '',
    hint:         existing?.hint          || '',
    responseType: existing?.response_type || 'select',
    optionsRaw:   existing?.options ? existing.options.join(', ') : '',
    flagRaw:      existing?.flag_values ? existing.flag_values.join(', ') : '',
    isRequired:   existing?.is_required ?? false,
    sortOrder:    existing?.sort_order ?? 0,
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    const payload = {
      category:     form.category,
      label:        form.label,
      hint:         form.hint || null,
      responseType: form.responseType,
      options:      form.responseType === 'select'
        ? form.optionsRaw.split(',').map(s => s.trim()).filter(Boolean)
        : null,
      flagValues: form.flagRaw
        ? form.flagRaw.split(',').map(s => s.trim()).filter(Boolean)
        : null,
      isRequired: form.isRequired,
      sortOrder:  parseInt(form.sortOrder) || 0,
    }
    try {
      if (existing) {
        await updateItem.mutateAsync({ id: existing.id, templateId, ...payload })
        toast.success('Item updated')
      } else {
        await createItem.mutateAsync({ templateId, ...payload })
        toast.success('Item added')
      }
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save')
    }
  }

  const isPending = createItem.isPending || updateItem.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">
            {existing ? 'Edit Item' : 'Add Item'}
          </h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[75vh]">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Category</label>
              <input type="text" value={form.category} onChange={set('category')} required
                placeholder="e.g. Fluids" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Sort order</label>
              <input type="number" value={form.sortOrder} onChange={set('sortOrder')} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Label</label>
            <input type="text" value={form.label} onChange={set('label')} required
              placeholder="e.g. Engine oil level" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Hint text</label>
            <input type="text" value={form.hint} onChange={set('hint')}
              placeholder="Optional helper text shown under the question" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Response type</label>
            <select value={form.responseType} onChange={set('responseType')} className={inputCls}>
              {RESPONSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {form.responseType === 'select' && (
            <>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                  Options <span className="text-[var(--text-muted)] font-normal">(comma-separated)</span>
                </label>
                <input type="text" value={form.optionsRaw} onChange={set('optionsRaw')}
                  placeholder="Low, OK, Full" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                  Flag values <span className="text-[var(--text-muted)] font-normal">(comma-separated — creates a vehicle issue)</span>
                </label>
                <input type="text" value={form.flagRaw} onChange={set('flagRaw')}
                  placeholder="Low" className={inputCls} />
              </div>
            </>
          )}
          {form.responseType === 'yesno' && (
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                Flag if answered <span className="text-[var(--text-muted)] font-normal">(Yes or No)</span>
              </label>
              <select value={form.flagRaw} onChange={set('flagRaw')} className={inputCls}>
                <option value="">— no auto-flag —</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.isRequired}
              onChange={e => setForm(p => ({ ...p, isRequired: e.target.checked }))}
              className="rounded" />
            <span className="text-sm text-[var(--text-secondary)]">Required</span>
          </label>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {isPending ? 'Saving…' : existing ? 'Save Changes' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Template row with expandable items ───────────────────────────────────────

function TemplateRow({ template, isSystem, onEdit, onDuplicate }) {
  const [open, setOpen]             = useState(false)
  const [showAddItem, setShowAddItem] = useState(false)
  const [editItem, setEditItem]     = useState(null)

  const { data: items = [], isLoading } = useCheckinTemplateItems(open ? template.id : null)
  const deleteTemplate = useDeleteCheckinTemplate()
  const deleteItem     = useDeleteCheckinTemplateItem()
  const updateTemplate = useUpdateCheckinTemplate()
  const duplicate      = useDuplicateCheckinTemplate()

  const handleDeleteTemplate = async () => {
    if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) return
    try {
      await deleteTemplate.mutateAsync(template.id)
      toast.success('Template deleted')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Cannot delete this template')
    }
  }

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Remove "${item.label}"?`)) return
    try {
      await deleteItem.mutateAsync({ id: item.id, templateId: template.id })
      toast.success('Item removed')
    } catch {
      toast.error('Failed to remove item')
    }
  }

  const handleToggleActive = async () => {
    try {
      await updateTemplate.mutateAsync({ id: template.id, isActive: !template.is_active })
      toast.success(template.is_active ? 'Template deactivated' : 'Template activated')
    } catch {
      toast.error('Failed to update')
    }
  }

  const handleDuplicate = async () => {
    try {
      const newTemplate = await duplicate.mutateAsync(template.id)
      toast.success('Template duplicated')
      onDuplicate(newTemplate)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to duplicate')
    }
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3.5">
        <button
          onClick={() => setOpen(o => !o)}
          className="flex-1 flex items-center gap-3 text-left min-w-0"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={clsx('text-sm font-semibold', !template.is_active && 'text-[var(--text-muted)] line-through')}>
                {template.name}
              </span>
              {isSystem && (
                <span className="px-1.5 py-0.5 text-xs bg-[var(--bg-overlay)] text-[var(--text-muted)] rounded">
                  System
                </span>
              )}
              {!template.is_active && (
                <span className="px-1.5 py-0.5 text-xs bg-gray-500/15 text-gray-400 rounded">Inactive</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {template.fuel_type && (
                <span className={clsx('px-2 py-0.5 text-xs font-medium rounded',
                  FUEL_STYLES[template.fuel_type] || 'bg-gray-500/15 text-gray-400')}>
                  {FUEL_LABELS[template.fuel_type] || template.fuel_type}
                </span>
              )}
              <span className={clsx('px-2 py-0.5 text-xs font-medium rounded',
                FREQ_STYLES[template.frequency] || 'bg-gray-500/15 text-gray-400')}>
                {FREQ_LABELS[template.frequency] || template.frequency}
              </span>
              <span className="text-xs text-[var(--text-muted)]">
                {template.item_count} item{template.item_count !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          {open ? <ChevronUp size={16} className="text-[var(--text-muted)] shrink-0" />
                : <ChevronDown size={16} className="text-[var(--text-muted)] shrink-0" />}
        </button>

        <div className="flex items-center gap-1 shrink-0">
          {!isSystem && (
            <button
              onClick={() => onEdit(template)}
              title="Edit template"
              className="p-1.5 text-[var(--text-muted)] hover:text-indigo-400 rounded-lg transition-colors"
            >
              <Edit2 size={14} />
            </button>
          )}
          <button
            onClick={handleDuplicate}
            disabled={duplicate.isPending}
            title="Duplicate template"
            className="p-1.5 text-[var(--text-muted)] hover:text-indigo-400 disabled:opacity-40 rounded-lg transition-colors"
          >
            <Copy size={14} />
          </button>
          {!isSystem && (
            <>
              <button
                onClick={handleToggleActive}
                className="px-2.5 py-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]
                           bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)] rounded-lg transition-colors"
              >
                {template.is_active ? 'Deactivate' : 'Activate'}
              </button>
              <button
                onClick={handleDeleteTemplate}
                className="p-1.5 text-[var(--text-muted)] hover:text-red-400 rounded-lg transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-[var(--border)]">
          {isLoading ? (
            <div className="p-4 space-y-2 animate-pulse">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-8 bg-[var(--bg-overlay)] rounded" />
              ))}
            </div>
          ) : (
            <>
              {items.length === 0 ? (
                <p className="px-4 py-3 text-sm text-[var(--text-muted)]">No items yet.</p>
              ) : (
                <div className="divide-y divide-[var(--border)]">
                  {items.map(item => (
                    <div key={item.id}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--bg-base)] transition-colors group">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider shrink-0">
                            {item.category}
                          </span>
                          <span className="text-sm text-[var(--text-primary)] truncate">{item.label}</span>
                          {item.is_required && (
                            <span className="text-red-400 text-xs shrink-0">*</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-[var(--text-muted)]">{item.response_type}</span>
                          {item.options?.length > 0 && (
                            <span className="text-xs text-[var(--text-muted)]">
                              — {item.options.join(', ')}
                            </span>
                          )}
                          {item.flag_values?.length > 0 && (
                            <span className="text-xs text-red-400">
                              flags: {item.flag_values.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                      {!isSystem && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button onClick={() => setEditItem(item)}
                            className="p-1 text-[var(--text-muted)] hover:text-indigo-400 transition-colors">
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => handleDeleteItem(item)}
                            className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {!isSystem && (
                <div className="px-4 py-3 border-t border-[var(--border)]">
                  <button
                    onClick={() => setShowAddItem(true)}
                    className="flex items-center gap-1.5 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    <Plus size={14} />
                    Add item
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showAddItem && (
        <ItemModal templateId={template.id} onClose={() => setShowAddItem(false)} />
      )}
      {editItem && (
        <ItemModal templateId={template.id} existing={editItem} onClose={() => setEditItem(null)} />
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CheckinTemplatesPage() {
  const { data: templates = [], isLoading } = useCheckinTemplates()
  const [showCreate, setShowCreate]       = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)

  const systemTemplates = templates.filter(t => !t.garage_id)
  const customTemplates = templates.filter(t =>  t.garage_id)

  const handleCloseModal = () => {
    setShowCreate(false)
    setEditingTemplate(null)
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">Check-In Templates</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Manage the check-in templates sent to vehicle owners for their periodic health checks.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                     text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus size={15} />
          New Template
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3 animate-pulse">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {customTemplates.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest">
                Your templates
              </h2>
              {customTemplates.map(t => (
                <TemplateRow
                  key={t.id}
                  template={t}
                  isSystem={false}
                  onEdit={setEditingTemplate}
                  onDuplicate={setEditingTemplate}
                />
              ))}
            </section>
          )}

          <section className="space-y-3">
            <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest">
              System templates
            </h2>
            <p className="text-xs text-[var(--text-muted)]">
              System templates are read-only. Use the duplicate button to create an editable copy.
            </p>
            {systemTemplates.map(t => (
              <TemplateRow
                key={t.id}
                template={t}
                isSystem
                onEdit={setEditingTemplate}
                onDuplicate={setEditingTemplate}
              />
            ))}
          </section>
        </>
      )}

      {(showCreate || editingTemplate) && (
        <TemplateModal existing={editingTemplate} onClose={handleCloseModal} />
      )}
    </div>
  )
}
